import { useState } from 'react'
import { Store, Loader2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Switch } from '@/shared/components/ui/switch'
import { notify } from '@/shared/utils'
import { requestNotificationPermission } from '@/shared/utils/browserNotification'
import { vendorApi } from '@/features/inventory/api/vendorApi'
import type { Vendor } from '@/shared/types'

interface ShopStatusToggleProps {
  vendorId: string
  vendorType: 'FOOD' | 'CLOTHING'
  shopState: string
}

export function ShopStatusToggle({ vendorId, vendorType, shopState }: ShopStatusToggleProps) {
  const queryClient = useQueryClient()
  const isOpen = shopState === 'OPEN' || shopState === 'KITCHEN_BUSY'
  const [isLoading, setIsLoading] = useState(false)

  const handleToggle = async (checked: boolean) => {
    // Request notification permission on user gesture (required by mobile browsers)
    if (checked) requestNotificationPermission()
    setIsLoading(true)
    try {
      const data = vendorType === 'FOOD'
        ? { kitchenState: checked ? 'OPEN' : 'CLOSED' }
        : { operationalState: checked ? 'OPEN' : 'CLOSED' }

      await vendorApi.updateShopStatus(vendorId, data)
      queryClient.setQueryData(['vendor', vendorId], (old: Vendor | null) =>
        old ? { ...old, shopState: checked ? 'OPEN' : 'CLOSED' } : old
      )
      notify.success(`Shop is now ${checked ? 'OPEN' : 'CLOSED'}`)
    } catch {
      notify.error('Failed to update shop status')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`rounded-xl p-5 flex items-center justify-between transition-colors ${isOpen ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-full ${isOpen ? 'bg-green-500' : 'bg-red-500'}`}>
          <Store className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className={`text-lg font-bold ${isOpen ? 'text-green-800' : 'text-red-800'}`}>
            {isOpen ? 'You\'re Online' : 'You\'re Offline'}
          </p>
          <p className={`text-sm ${isOpen ? 'text-green-600' : 'text-red-600'}`}>
            {vendorType === 'FOOD' ? 'Kitchen' : 'Store'} is {isOpen ? 'accepting orders' : 'not accepting orders'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {isLoading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
        <Switch checked={isOpen} onCheckedChange={handleToggle} disabled={isLoading} className="scale-125" />
      </div>
    </div>
  )
}
